import * as React from 'react';
import { useSelector, useDispatch } from "react-redux";
import '../styles.css';
//internal and external components import
import { Table } from '@mui/material';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import StatusIndicatorSmall from '../../StatusIndicatorSmall';
import ProgressBar from '../../ProgressBar';
import { sortData } from '../../../common/SortSupport';
import { EnhancedTableHead } from '../../EnhancedTableHead';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCirclePlus, faDownLeftAndUpRightToCenter, faTrash } from '@fortawesome/free-solid-svg-icons';
import { Box } from '@mui/material';
import { CustomTooltip } from '../../CustomTooltip';
import TableSkeleton from '../../TableSkeleton';
import { formatDate, selectedProjectActionRegex, selectedProjectRegex } from '../../../common/StaticExports';
import CommentsDocsActionsIconWrapper from '../../CommentsDocsActionsIconWrapper';
import { useTranslation } from 'react-i18next';
import { resetSelectedProject, setProjects, setSelectedProject } from '../../../redux/reducers/actionPlan';
import ButtonDefault from '../../ButtonDefault';
import NewProjectDetailPopper from '../../../containers/NewProjectDetailPopper';
import NestedThreeDotMenu from '../../NestedThreeDotMenu';
import { faEyeSlash } from '@fortawesome/free-regular-svg-icons';
import { archiveProject, deleteProject, getProjects } from '../../../api/projects';
import { useLocation, useNavigate } from 'react-router';


export default function ProjectsTable(props: any) {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const clubDetails = useSelector((state:any) => state.clubDetails);
  const [t] = useTranslation();
  const [addNewProject, setAddNewProject] = React.useState(false);
  const data = useSelector((state:any) => state.actionPlan.projects);
  const selectedProject = useSelector((state:any) => state.actionPlan.selectedProject);
  const [isLoadingProjects, setIsLoadingProjects] = React.useState(false);
  const projectsSearchString = useSelector((state:any) => state.actionPlan.projectsSearchString);
  const selectedArchivedStatuses = useSelector((state:any) => state.actionPlan.projectsArchivedFilter);
  const selectedDepartments = useSelector((state:any) => state.common.departmentsFilter);
  const isAdmin = useSelector((state:any) => state.common.isAdmin);
  const isActions = useSelector((state: any) => state.actionPlan.isActions);

  //supporting contents
  const [order, setOrder] = React.useState('asc');
  const [orderBy, setOrderBy] = React.useState('id');

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: any,
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    
  };

  const visibleRows:any = React.useMemo(
    () =>
    {
      if(data?.length > 0) {
        let tempData = data;

        if((projectsSearchString && projectsSearchString?.trim() !== '') ||
           (selectedArchivedStatuses && selectedArchivedStatuses.length > 0) ||
           (selectedDepartments && selectedDepartments.length > 0)) {
          tempData = data.filter((item:any) => {
            const archivedStatusIds = selectedArchivedStatuses?.map((status:any) => status.id);
            const selectedDeptIds = selectedDepartments?.map((dept:any) => dept.id);
            return (((projectsSearchString && projectsSearchString?.trim() !== '') && 
                      (item?.name?.toLowerCase().includes(projectsSearchString?.toLowerCase()) ||
                      (item?.ownerName && item.ownerName?.toLowerCase().includes(projectsSearchString?.toLowerCase())) ||
                      (item?.departmentName && item.departmentName?.toLowerCase().includes(projectsSearchString?.toLowerCase())))) ||
                    ((selectedArchivedStatuses && selectedArchivedStatuses.length > 0) && 
                      ((item.isArchived && archivedStatusIds.includes(2)) ||
                      (!item.isArchived && archivedStatusIds.includes(1)) ||
                      ((!item.isArchived || item.isArchived) && archivedStatusIds.includes(0)))) ||
                    ((selectedDepartments && selectedDepartments.length > 0) && 
                      (selectedDeptIds.includes(item.departmentId)))
            );
          });
        }


        return sortData(tempData, orderBy, order);
      }
    },
    [order, orderBy, data, projectsSearchString, selectedArchivedStatuses, selectedDepartments],
  );

  var head = [
    {
      key: '', displayName: 'id',
    },
    {
      key: 'name', displayName: 'project',
    },
    {
      key: '', displayName: '',
    },
    {
      key: 'ragb', displayName: 'status',
    },
    {
      key: 'ownerName', displayName: 'owner',
    },
    {
      key: 'departmentName', displayName: 'department',
    },
    {
      key: 'dueDate', displayName: 'due date',
    },
    {
      key: 'progress', displayName: 'progress',
    }
  ];
  if (!isAdmin) {
    head.push({
      key: '', displayName: '',
    });
  }

  const threeDotMenuOptions =
  [
    {label: 'Archive Project', icon: faEyeSlash, isDisabled: false},
    {label: 'Delete Project', icon: faTrash, isDisabled: false},
  ];
  const handleThreedotMenuClick = async (selctedAction:any, project:any) => {
    switch(selctedAction) {
      case 'Delete Project':
        await deleteProject({ projectId: project?.id });
        fetchProjects();
        break;
      case 'Archive Project':
        await archiveProject({ projectId: project?.id });
        fetchProjects();
        break;
    }
  }

  const fetchProjects = async () => {
    setIsLoadingProjects(true);
    const response = await getProjects({ archivedStatus: 0, clubId: clubDetails?.id });
    dispatch(setProjects(response));
    setIsLoadingProjects(false);
  };

  React.useEffect(() => {
    !isLoadingProjects &&
    !data && clubDetails && 
    clubDetails?.id &&
    !isActions &&
    fetchProjects();

    if (selectedProjectRegex.test(location.pathname) ||
        selectedProjectActionRegex.test(location.pathname)) {
          const projectId = location.pathname.split('/')[3];
          if (data && data.length > 0) {
            const tempObj = data.find((d:any) => d.id == projectId);
            if (tempObj) {
              dispatch(setSelectedProject({...tempObj, projectIndex: data.indexOf(tempObj)+1}));
            } 
          } else {
              const tempObj:any = {id: projectId, projectIndex: 1};
              dispatch(setSelectedProject(tempObj));
            }
        }
  }, [clubDetails, isActions, location]);

  const manageTableExpand = () => {
    dispatch(resetSelectedProject());
    fetchProjects();
    navigate(`/performanceplan/projects`);
  }

  const manageTableShrink = (d: any, index:any) => {
    dispatch(resetSelectedProject());
    dispatch(setSelectedProject({...d, projectIndex: index+1}));
    navigate(`/performanceplan/projects/${d.id}`);
  }  

  //table contents 
  return (
    <React.Fragment>
      <Box sx={{ height: '100%', position: 'relative' }}>
        <NewProjectDetailPopper isOpen={addNewProject} handleClickAway={() => {setAddNewProject(false); fetchProjects();}} />
        {(selectedProject != null && !(Object.keys(selectedProject).length === 0)) && <FontAwesomeIcon className='icon tableExpandIcon' icon={faDownLeftAndUpRightToCenter} onClick={() => manageTableExpand()} />}
        <TableContainer sx={{height: '100%', width: (selectedProject != null && !(Object.keys(selectedProject).length === 0)) ? 'fit-content' : 'auto', overflow: (selectedProject == null || Object.keys(selectedProject).length === 0) ? 'auto' : 'hidden'}}>
          <Table stickyHeader sx={{ width: (selectedProject == null || Object.keys(selectedProject).length === 0) ? '100%' : 'auto', boxShadow: 'none' }} size="small" aria-label="a dense table">
            <EnhancedTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              bg={'#fff'}
              tableHeads={(selectedProject == null || Object.keys(selectedProject).length === 0) ? head : [head[0]]}
            />
            <TableBody>
              {
                (!isLoadingProjects && visibleRows?.length) ?
                visibleRows.map((d: any, i: number) => (
                  <TableRow
                    className='hoverBG'
                    key={`projects-dynamic-table-row-${i}`}
                  >
                    <CustomTooltip title={d.name} className={selectedProject != null && !(Object.keys(selectedProject).length === 0) ? 'capitalize' : 'displayNone'} arrow followCursor placement='top-start'>
                      <TableCell className={(selectedProject != null && !(Object.keys(selectedProject).length === 0) && selectedProject.id === d.id) ? 'fw900 backGroundColorGrey' : ''}
                    onClick={() => manageTableShrink(d, i)}>{`P${i+1}`}</TableCell>
                    </CustomTooltip>
                    {
                      (selectedProject == null || Object.keys(selectedProject).length === 0) && <>
                      <TableCell className='capitalize' sx={{cursor: 'pointer', width: '400px'}}
                    onClick={() => manageTableShrink(d, i)}>
                        {d.name}
                      </TableCell>
                      <TableCell className='fs12' onClick={() => manageTableShrink(d, i)} sx={{ width: '120px' }}><CommentsDocsActionsIconWrapper actions={d?.noOfActions ? d?.noOfActions : 0} comments={d?.comments ? d?.comments : 0} /></TableCell>
                      <TableCell onClick={() => manageTableShrink(d, i)}><StatusIndicatorSmall reportType={'rabg'} status={d?.ragb} /></TableCell>
                      <TableCell onClick={() => manageTableShrink(d, i)}>
                        {d?.ownerName ? d?.ownerName : '-'}
                      </TableCell>
                      <TableCell onClick={() => manageTableShrink(d, i)}>
                        {d?.departmentName ? d?.departmentName : '-'}
                      </TableCell>
                      <TableCell onClick={() => manageTableShrink(d, i)} sx={{ width: '120px' }}>
                        {d?.dueDate ? formatDate(d?.dueDate) : '-'}
                      </TableCell>
                      <TableCell onClick={() => manageTableShrink(d, i)}>
                        <ProgressBar percent={d?.progress}/>
                      </TableCell>
                      {
                        !isAdmin &&
                        !d.isRestricted &&
                        <TableCell>
                          <NestedThreeDotMenu 
                          options={threeDotMenuOptions} 
                          manageClick={(e:any) => {handleThreedotMenuClick(e, d)}} />
                        </TableCell>
                      }
                      </>
                    } 
                  </TableRow>
                ))
                :
                !isLoadingProjects && visibleRows?.length === 0 ?
                <TableRow>
                  <TableCell colSpan={head?.length} align="center">
                    {t('NO_RECORDS_FOUND')}
                  </TableCell>
                </TableRow>
                :
                isLoadingProjects &&
                <TableSkeleton rowCount={13} cellCount={head?.length} />
              }
            </TableBody>
          </Table>
        </TableContainer>
        {
          (selectedProject == null || Object.keys(selectedProject).length === 0) && !isAdmin &&
          <Box sx={{ float: 'left', mt: 2 }}>
            <ButtonDefault
            variant={'outlined'}
            value={t('ADD_PROJECT')}
            icon={<FontAwesomeIcon icon={faCirclePlus} />}
            manageOnClick={() => {setAddNewProject(true)}}
            disabled={addNewProject}
            />
          </Box>
        }
      </Box>
    </React.Fragment>
  );
}